// frontend/src/components/admin/CompanyDetails.js
import React, { useState, useEffect, useRef } from 'react';
import '../masters/CustomerForm.css';
import { toast } from 'react-toastify';
import { openFilePreview } from '../common/FilePreviewModal';
import { usePermissions } from '../../services/PermissionsContext';

const API_URL = '/api/admin/company-details/';

const getAuthHeaders = (isFormData = false) => {
    const headers = {
        'Authorization': `Bearer ${localStorage.getItem('kk_token')}`
    };
    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
    }
    return headers;
};

const CompanyDetails = () => {
    const { hasPermission } = usePermissions();
    const [details, setDetails] = useState({
        company_name: '',
        company_address: '',
        company_phone: '',
        company_email: '',
        logo: null,
    });
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const logoInputRef = useRef(null);

    const canEdit = hasPermission('COMPANY DETAILS', 'can_edit') || hasPermission('COMPANY DETAILS', 'can_create');

    useEffect(() => {
        if (!hasPermission('COMPANY DETAILS', 'can_view')) return;
        const load = async () => {
            try {
                const res = await fetch(API_URL, { headers: getAuthHeaders() });
                if (res.ok) {
                    const data = await res.json();
                    setDetails(prev => ({ ...prev, ...data }));
                    if (data.logo) setLogoPreview(data.logo);
                }
            } catch (err) {
                console.error('Failed to load company details:', err);
            }
        };
        load();
    }, [hasPermission]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        const finalValue = name === 'company_email' ? value.toLowerCase() : value.toUpperCase();
        setDetails(prev => ({ ...prev, [name]: finalValue }));
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));
    };

    const handleLogoPreview = () => {
        if (!logoPreview) return;
        let fileName = 'Company_Logo';
        if (logoFile) {
            fileName = logoFile.name;
        } else if (typeof logoPreview === 'string') {
            const last = logoPreview.split('/').pop();
            if (last && last.includes('.')) fileName = last.split('?')[0];
        }
        openFilePreview(logoPreview, fileName);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canEdit) {
            toast.error('You do not have permission to save company details.');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const formData = new FormData();
            formData.append('company_name', details.company_name || '');
            formData.append('company_address', details.company_address || '');
            formData.append('company_phone', details.company_phone || '');
            formData.append('company_email', details.company_email || '');
            if (logoFile) {
                formData.append('logo', logoFile);
            }
            const res = await fetch(API_URL, {
                method: 'POST',
                headers: getAuthHeaders(true),
                body: formData,
            });
            if (res.ok) {
                const data = await res.json();
                setDetails(prev => ({ ...prev, ...data }));
                setLogoFile(null);
                if (data.logo) setLogoPreview(data.logo);
                if (logoInputRef.current) logoInputRef.current.value = '';
                toast.success('Company details saved successfully!');
            } else {
                throw new Error('Failed to save company details');
            }
        } catch (err) {
            console.error('Error saving company details:', err);
            setError('Error saving company details.');
            toast.error('Error saving company details');
        } finally {
            setSaving(false);
        }
    };

    if (!hasPermission('COMPANY DETAILS', 'can_view')) {
        return (
            <div className="customer-form-container">
                <h1>Permission Denied</h1>
                <p>You do not have permission to view this page.</p>
            </div>
        );
    }

    return (
        <div className="customer-form-container">
            <h1>Company Details</h1>

            {error && <div className="form-error">{error}</div>}

            <form onSubmit={handleSubmit}>
                <div className="section">
                    <h2>Company Information</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                        <div className="row" style={{ gridTemplateColumns: '2fr 1fr 1fr', alignItems: 'flex-end' }}>
                            <div>
                                <label className="required">Company Name</label>
                                <input
                                    type="text"
                                    name="company_name"
                                    value={details.company_name || ''}
                                    onChange={handleChange}
                                    required
                                    readOnly={!canEdit}
                                />
                            </div>
                            <div>
                                <label>Phone Number</label>
                                <input
                                    type="tel"
                                    name="company_phone"
                                    value={details.company_phone || ''}
                                    onChange={handleChange}
                                    placeholder="+255-XXX-XXX-XXX"
                                    readOnly={!canEdit}
                                />
                            </div>
                            <div>
                                <label>Email Address</label>
                                <input
                                    type="email"
                                    name="company_email"
                                    value={details.company_email || ''}
                                    onChange={handleChange}
                                    readOnly={!canEdit}
                                    style={{ textTransform: 'lowercase' }}
                                />
                            </div>
                        </div>
                        <div className="row" style={{ gridTemplateColumns: '2fr 1fr', alignItems: 'flex-end' }}>
                            <div>
                                <label>Company Address</label>
                                <textarea
                                    name="company_address"
                                    value={details.company_address || ''}
                                    onChange={handleChange}
                                    rows="3"
                                    placeholder="P.O.BOX ..., CITY, COUNTRY"
                                    readOnly={!canEdit}
                                />
                            </div>
                            <div>
                                <label>Company Logo</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    ref={logoInputRef}
                                    onChange={handleLogoChange}
                                    disabled={!canEdit}
                                />
                                {logoPreview && (
                                    <div className="file-links" style={{ marginTop: '5px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <img
                                            src={logoPreview}
                                            alt="Company Logo"
                                            style={{ height: '40px', border: '1px solid #ddd', borderRadius: '4px', padding: '2px', background: '#fff' }}
                                        />
                                        <span onClick={handleLogoPreview} className="preview-link">
                                            View Logo
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {canEdit && (
                    <div className="buttons">
                        <button type="submit" disabled={saving}>
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                )}
            </form>
        </div>
    );
};

export default CompanyDetails;
